import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { useHttpClient } from 'src/lib/http-client/use-http-client';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';

import { GridSortModel } from '@material-ui/data-grid';

import ContractRecordDto from 'src/contracts/contracts-list/contract-record-dto';

export const useDamagesDetailsSearch = (
    hasAccessToSearch: boolean,
    globalSearchTerm: string,
    allowedCustomerConnections?: string[],
    allowedCustomers?: string[],
    allowedDivisions?: string[],
    allowedProducts?: string[],
    allowedRisks?: string[],
    allowedStatuses?: string[],
    sortModel?: GridSortModel,
    cityAndPostcode?: string,
    damageDate?: ParsableDate,
    page?: number,
    rowsPerPage?: number
): {
    generalAmountLoading: boolean;
    generalAmount: number;
    searchLoading: boolean;
    searchResults: {
        result: ContractRecordDto[];
    };
} => {
    const httpClient = useHttpClient();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation(['common', 'errors']);

    const [searchResults, setSearchResults] = useState<{
        result: ContractRecordDto[];
        count: number;
    }>({ result: [], count: 0 });
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResultGeneralAmountData, setSearchResultsGeneralAmountData] = useState<{
        isLoading: boolean;
        count: number;
    }>({ isLoading: false, count: null });

    const makeRequestToGetGeneralAmount = useCallback(async () => {
        try {
            setSearchResultsGeneralAmountData({ count: null, isLoading: true });
            const data = await httpClient.post<{
                count: number;
            }>(`get-damages/details-search/count`, {
                globalSearchTerm,
                date: damageDate ? moment(damageDate).toISOString() : undefined,
                cityAndPostcode: cityAndPostcode || undefined,
                allowedCustomers: allowedCustomers || undefined,
                allowedDivisions: allowedDivisions || undefined,
                allowedProducts: allowedProducts || undefined,
                allowedCustomerConnections: allowedCustomerConnections || undefined,
                allowedRisks: allowedRisks || undefined,
                allowedStatuses: allowedStatuses || undefined,
            });
            setSearchResultsGeneralAmountData({ count: data.count, isLoading: false });
        } catch {
            setSearchResultsGeneralAmountData({ count: null, isLoading: false });
        }
    }, [
        globalSearchTerm,
        hasAccessToSearch,
        allowedStatuses,
        allowedProducts,
        allowedCustomerConnections,
        allowedCustomers,
        allowedRisks,
        allowedDivisions,
        cityAndPostcode,
        damageDate,
    ]);

    useEffect(() => {
        if (hasAccessToSearch) {
            makeRequestToGetGeneralAmount();
        }
    }, [makeRequestToGetGeneralAmount, hasAccessToSearch]);

    useEffect(() => {
        if (hasAccessToSearch) {
            setSearchLoading(true);
            httpClient
                .post<{
                    result: ContractRecordDto[];
                    count: number;
                }>(`get-damages/details-search`, {
                    globalSearchTerm,
                    skip:
                        typeof page === 'number' && typeof rowsPerPage === 'number'
                            ? (page + 1) * rowsPerPage - rowsPerPage
                            : undefined,
                    take: typeof rowsPerPage === 'number' ? rowsPerPage + 1 : undefined,
                    date: damageDate ? moment(damageDate).toISOString() : undefined,
                    cityAndPostcode: cityAndPostcode || undefined,
                    allowedCustomers: allowedCustomers || undefined,
                    allowedDivisions: allowedDivisions || undefined,
                    allowedProducts: allowedProducts || undefined,
                    allowedCustomerConnections: allowedCustomerConnections || undefined,
                    allowedRisks: allowedRisks || undefined,
                    allowedStatuses: allowedStatuses || undefined,
                    sortField:
                        Array.isArray(sortModel) && sortModel.length > 0
                            ? sortModel[0].field
                            : undefined,
                    isAscending:
                        Array.isArray(sortModel) && sortModel.length > 0
                            ? sortModel[0].sort === 'asc'
                            : undefined,
                })
                .then((data: { result: ContractRecordDto[]; count: number }) => {
                    if (!data || data?.result?.length === 0) {
                        enqueueSnackbar(t('nothingWasFound'), {
                            variant: 'info',
                        });
                    }
                    setSearchResults(data);
                })
                .catch(() => {
                    enqueueSnackbar(t('errors:unknownError'), {
                        variant: 'error',
                    });
                })
                .finally(() => {
                    setSearchLoading(false);
                });
        }
    }, [
        globalSearchTerm,
        hasAccessToSearch,
        allowedStatuses,
        allowedProducts,
        allowedCustomerConnections,
        allowedCustomers,
        allowedRisks,
        allowedDivisions,
        sortModel,
        cityAndPostcode,
        damageDate,
        page,
        rowsPerPage,
    ]);

    return {
        searchLoading,
        searchResults,
        generalAmountLoading: searchResultGeneralAmountData.isLoading,
        generalAmount: searchResultGeneralAmountData.count,
    };
};
